/**
 * Logger utility for frontend
 * This centralizes all logging functionality and allows for future enhancements like:
 * - Sending logs to a backend service
 * - Adding log levels
 * - Formatting logs consistently
 */

const LOG_LEVELS = {
  ERROR: 'ERROR',
  WARN: 'WARN',
  INFO: 'INFO',
  DEBUG: 'DEBUG'
}

export { LOG_LEVELS }

const isProduction = process.env.NODE_ENV === 'production'

const noop = () => {}

const STYLES = {
  ERROR: 'color: #FF5252; font-weight: bold',
  WARN: 'color: #FFC107; font-weight: bold',
  INFO: 'color: #4CAF50; font-weight: bold',
  DEBUG: 'color: #2196F3; font-weight: bold',
  TIMESTAMP: 'color: #9E9E9E',
  RESET: ''
}

/**
 * Frontend-specific log formatter that adds browser console styling
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Error|object} [error] - Optional error object or additional context
 */
function frontendFormatter (level, message, error = null) {
  const timestamp = new Date().toISOString()

  const messageParts = [`%c[${timestamp}]%c %c[${level}]%c ${message}`]
  const styles = [STYLES.TIMESTAMP, STYLES.RESET, STYLES[level], STYLES.RESET]

  let additionalInfo = ''

  if (error) {
    if (error instanceof Error) {
      additionalInfo += `\n  Error: ${error.message}`
      if (error.stack) {
        additionalInfo += `\n  Stack: ${error.stack}`
      }
    } else if (typeof error === 'object') {
      try {
        additionalInfo += `\n  Context: ${JSON.stringify(error)}`
      } catch (e) {
        additionalInfo += '\n  Context: [Object cannot be stringified]'
      }
    } else {
      additionalInfo += `\n  Context: ${error}`
    }
  }

  const formattedParts = additionalInfo ? [...messageParts, additionalInfo] : messageParts

  if (level === LOG_LEVELS.ERROR) {
    console.error(...formattedParts, ...styles)
  } else if (level === LOG_LEVELS.WARN) {
    console.warn(...formattedParts, ...styles)
  } else {
    console.log(...formattedParts, ...styles)
  }

  return { level, message, error }
}

/**
 * Create a standardized logger with environment-specific formatters
 * @param {object} options - Configuration options
 * @param {function} options.formatter - Function to format log messages
 * @returns {object} Logger instance with standard methods
 */
function createLogger (options = {}) {
  const { formatter } = options

  /**
   * Log an error message (only in non-production)
   * @param {string} message - Error message
   * @param {Error|object} [error] - Optional error object or additional context
   */
  function error (message, error = null) {
    if (isProduction) return null
    return formatter(LOG_LEVELS.ERROR, message, error)
  }

  /**
   * Log a warning message (only in non-production)
   * @param {string} message - Warning message
   * @param {object} [context] - Optional additional context
   */
  function warn (message, context = null) {
    if (isProduction) return null
    return formatter(LOG_LEVELS.WARN, message, context)
  }

  /**
   * Log an info message (only in non-production)
   * @param {string} message - Info message
   * @param {object} [context] - Optional additional context
   */
  function info (message, context = null) {
    if (isProduction) return null
    return formatter(LOG_LEVELS.INFO, message, context)
  }

  /**
   * Log a debug message (only in non-production)
   * @param {string} message - Debug message
   * @param {object} [context] - Optional additional context
   */
  function debug (message, context = null) {
    if (isProduction) return noop

    return formatter(LOG_LEVELS.DEBUG, message, context)
  }

  return {
    error,
    warn,
    info,
    debug,
    LOG_LEVELS
  }
}

const logger = createLogger({
  formatter: frontendFormatter
})

export const { error, warn, info, debug } = logger
export default logger
